import { useShoppingCart, useStores } from "@sushicorp/contexts";
import { useCatalogues } from "@sushicorp/contexts";
import { useProducts } from "@sushicorp/contexts";
import { useVendors } from "@sushicorp/contexts";
import { useFetchProductDetails } from "@sushicorp/services";
import { sanitizeQueryParams } from "@sushicorp/utils";
import { getFullPath } from "@sushicorp/utils";
import { ShareButton } from "artisn-ui-react";
import Head from "next/head";
import { useRouter } from "next/router";
import React from "react";

import Styles from "./Product.styles";
import { ProductProps as Props } from "./Product.types";
import ProductMain from "../ProductMain/ProductMain";
import Footer from "components/global/Footer/Footer";
import GoBack from "components/global/GoBack/GoBack";
import Navbar from "components/global/Navbar/Navbar";
import CONSTANTS from "config/constants";
import useAuth from "contexts/auth/auth.context.hooks";
import useI18n from "hooks/useI18n";
import { notify } from "utils/common.utils";
import { dismissAddToCartNotification } from "utils/notifications.utils";

import ShareSVG from "../../../../public/assets/images/share.svg";

const { TITLE, FEATURE_FLAGS } = CONSTANTS;
const { WITH_SHARE_PRODUCT } = FEATURE_FLAGS;

const Product: React.FC<Props> = props => {
  const { selectedVendorId } = useVendors();
  const { query, locale } = useRouter();
  const { selectedCatalogueId } = useCatalogues();
  const { selectedStore } = useStores();
  const { selectedProduct } = useProducts();
  const { isAnonymous } = useAuth();
  const { setOpenShoppingCartDrawer } = useShoppingCart();
  const t = useI18n();
  const { productId = "" } = sanitizeQueryParams(query);
  const { storeId } = selectedStore ?? {};

  const productsDetailsResponse = useFetchProductDetails(
    productId,
    {
      catalogueId: selectedCatalogueId,
      storeId: storeId ?? 0,
      vendorId: selectedVendorId,
      withCategories: true
    },
    notify
  );

  const { data: productData, isLoading, isError } = productsDetailsResponse;
  const product = selectedProduct ?? productData;
  const { description, images, name = "" } = product ?? {};

  const showShoppingCartDrawer = () => {
    setOpenShoppingCartDrawer(true);
    dismissAddToCartNotification();
  };

  const shareButtonNode = WITH_SHARE_PRODUCT ? (
    <ShareButton
      className="Product__shareButton"
      config={{
        title: name,
        text: name,
        url: `${getFullPath(product)}`,
        dialogTitle: "Comparte este producto con otra persona"
      }}
    >
      <div className="ProductMain__shareButton-button__content">
        <div className="ProductMain__shareButton-button__icon-container">
          <ShareSVG />
        </div>
        <p className="ProductMain__shareButton-button__text">{t.cart.share}</p>
      </div>
    </ShareButton>
  ) : null;

  return (
    <Styles className="Product">
      <Head>
        <title>
          {name} | {TITLE}
        </title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`${name} | ${TITLE}`} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={images?.[0]?.url} />
        <meta property="og:locale" content={locale ?? "es"} />
        <meta property="og:url" content={getFullPath()} />
      </Head>
      <header className="Product__header">
        <Navbar
          title=""
          className="Product__navbar"
          showBrand
          product={product}
        />
      </header>
      <main className="Product__main">
        <div className="Product__main__header">
          <div className="Product__topActions">
            <GoBack className="Product__goBack" title="Atrás" />
            {shareButtonNode}
          </div>
        </div>
        <ProductMain
          isPageStyle
          productId={productId}
          isLoading={isLoading}
          isError={isError}
          isSignedIn={!isAnonymous}
          product={product}
          showShoppingCartDrawer={showShoppingCartDrawer}
        />
      </main>
      <footer className="Product__footer">
        <Footer />
      </footer>
    </Styles>
  );
};

Product.defaultProps = {};

export default Product;
